import React, { useState, useEffect, useRef } from "react";

import ClearIcon from "@mui/icons-material/Clear";
import LocalPrintshopOutlinedIcon from "@mui/icons-material/LocalPrintshopOutlined";
import { Box, IconButton, Typography } from "@mui/material";
import Grid from "@mui/material/Grid";
import { Form, Formik } from "formik";
import { useNavigate, useParams } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import styled from "styled-components";

import { postAllMedicationData, getMedicationById, updateMedication } from "../../Api/Services/MedicationServices";
import { getPetNameByWorkspaceIdPres } from "../../Api/Services/PetServices";
import {
  createPharmacy,
  getPharmacyById,
  getPharmacyByWorkspaceId,
  updatePharmacy,
} from "../../Api/Services/PharmacyServices";
import { getAllVetnamePres } from "../../Api/Services/VetServices";
import useAuth from "../../ContextProvider/AuthProvider";
import {
  FormHeaderBox,
  FormHeaderTitle,
  Body,
  Column,
  newSmallFieldBoxStyle,
  ButtonBoxStyle,
  CancelButton,
} from "../../Css";
import { PrescriptionFormValidation, EditPrescriptionFormValidation } from "../../From/Validation";
import {
  dosageOptions,
  durationOptions,
  frequencyOptions,
} from "../../Options";
import { formatDate } from "../../Utils";
import AutoComplete from "../Common/AutoComplete";
import CustomButton from "../Common/Button";
import FormGroup from "../Common/FormGroup";
import FormLayout from "../Common/FormHeader";
import InputField from "../Common/InputField";
import CustomPhoneInput from "../Common/PhoneInput";
import  { showSnackbar } from "../Common/Snackbar";
import TextArea from "../Common/TextArea";
import "react-phone-input-2/lib/style.css";
import "./input.css";
import "../pet-information/petPrint.css";
//import axios from "axios";
import { useTheme } from "@mui/material/styles";

const defaultValue = {
  type: "",
  medication_name: "",
  durtion: "",
  dosage: "",
  frequency: "",
  start_date: "",
  reason: "",
  prescripton_number: "",
  pet_id: "",
  vet_id: "",
  pharmacy_id: "",
  pharmacy_contact: "",
  durationMeasurement: "",
  dosageMeasurement: "",
  frequencyMeasurement: "",
  pharmacy_name: "",
};

export default function PrescriptForm() {
  const [initialValue, setInitialValue] = useState(defaultValue);
  const formRef = useRef();
  const componentPDF = useRef();
  const { workspaceId: w_id } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const theme = useTheme();

  // Determine if this is edit mode
  const isEditMode = !!id;

  const [open, setOpen] = useState(false);
  const maxDate = new Date().toISOString().substr(0, 10);

  const [nameOptions, setNameOptions] = useState([]);
  const [vetNameOptions, setVetNameOptions] = useState([]);

  //Pharmacy name
  const [pharmacyOptions, setPharmacyOptions] = useState([]);
  const [pharmacy_id, setPharmacy_id] = useState("");
  const [pharmacyOpen, setPharmacyOpen] = useState(false);

  // Load existing data for edit mode
  const loadPrescriptionDetails = async () => {
    if (!isEditMode) return;
    
    try {
      const response = await getMedicationById(id);
      const prescriptData = response?.data?.data?.[0];
      const dosageArray = prescriptData.dosage.match(/^([\d.]+)(\D+)$/);
      const frequencyArray = prescriptData.frequency.match(/^([\d.]+)(\D+)$/);
      const durationArray = prescriptData.durtion.match(/^([\d.]+)(\D+)$/);

      setInitialValue({
        ...initialValue,
        pet_id: prescriptData.pet_id,
        type: prescriptData?.type,
        medication_name: prescriptData.medication_name,
        durtion: durationArray?.[1],
        dosage: dosageArray?.[1],
        frequency: frequencyArray?.[1],
        start_date: formatDate(prescriptData.start_date),
        reason: prescriptData.reason,
        prescripton_number: prescriptData.prescripton_number,
        vet_id: prescriptData?.vet_id,
        pharmacy_id: prescriptData.pharmacy_id,
        durationMeasurement: durationArray?.[2],
        dosageMeasurement: dosageArray?.[2],
        frequencyMeasurement: frequencyArray?.[2],
      });

      // Load pharmacy details
      const pharmacyResponse = await getPharmacyById(prescriptData.pharmacy_id);
      const pharmacyData = pharmacyResponse.data.data[0];
      formRef.current?.setFieldValue("pharmacy_name", pharmacyData.pharmacy_name);
      formRef.current?.setFieldValue("pharmacy_contact", pharmacyData.pharmacy_contact);
    } catch (error) {
      showSnackbar({
        type: "error",
        message: "Failed to load prescription details.",
      });
    }
  };

  const handlePharmacyName = async (e, newvalue) => {
    const selectedValue = newvalue?.value;

    if (selectedValue === "Add a new Pharmacy") {
      setPharmacy_id(""); // Clear the pharmacy_id state when "Add a new Pharmacy" is selected
      setPharmacyOpen(true);
      formRef.current?.setFieldValue("pharmacy_id", "");
      formRef.current?.setFieldValue("pharmacy_contact", "");
    } else {
      setPharmacy_id(selectedValue);
      setPharmacyOpen(false);
      try {
        const res = await getPharmacyById(selectedValue);
        const data = res.data.data[0];
        if (data) {
          formRef.current?.setFieldValue("pharmacy_contact", data.pharmacy_contact);
        } else {
          formRef.current?.setFieldValue("pharmacy_contact", "");
        }
      } catch (err) {
        console.error(err);
      }
    }
  };

  //Add new Vet
  const handleSelectChange = (e, newValues) => {
    const selectedValue = newValues.value;
    if (selectedValue === "vet") {
      navigate("/vet-and-therapist/form");
    } else {
      formRef.current.setFieldValue("vet_id", newValues?.value);
    }
  };

  // Handle vet selection in edit mode
  const handleVetSelection = (e, newValues) => {
    formRef.current.setFieldValue("vet_id", newValues?.value);
  };

  // Handle pharmacy selection in edit mode
  const handlePharmacySelection = async (e, newValues) => {
    formRef.current.setFieldValue("pharmacy_id", newValues?.value);
    // Load pharmacy contact details
    if (newValues?.value) {
      try {
        const res = await getPharmacyById(newValues.value);
        const data = res.data.data[0];
        if (data) {
          formRef.current?.setFieldValue("pharmacy_contact", data.pharmacy_contact);
          formRef.current?.setFieldValue("pharmacy_name", data.pharmacy_name);
        }
      } catch (err) {
         showSnackbar({
        type: "error",
        message: "Pharmacy add failed.",
      });
      }
    }
  };

  //sendPharmacyData function
  const sendPharmacyData = async (values, setSubmitting) => {
    try {
      const formData = {
        pharmacy_name: values?.pharmacy_name,
        pharmacy_contact: values?.pharmacy_contact,
        w_id,
      };
      const res = await createPharmacy(formData);
      const pharmacyId = res.data.Pharmacy_data.id; // Access the ID from the response
      formRef.current?.setFieldValue("pharmacy_id", pharmacyId);
      return pharmacyId;
    } catch (err) {
      showSnackbar({
        type: "error",
        message: "Pharmacy add failed.",
      });
      setSubmitting(false);
      throw err;
    }
  };

  const sendMedicationData = async (values, pharmacy_id) => {
    const value = {
      ...values,
      durtion: values?.durtion + " " + values?.durationMeasurement,
      dosage: values?.dosage + " " + values?.dosageMeasurement,
      frequency: values?.frequency + " " + values?.frequencyMeasurement,     
      pharmacy_id: pharmacy_id,
    };

    try {
      await postAllMedicationData(value);
      showSnackbar({
        type: "success",
        message: "Prescription added successfully.",
      });
    } catch (err) {
      showSnackbar({
        type: "error",
        message: "Prescription add failed.",
      });
      console.error(err);
      throw err;
    }
  };

  // Update existing prescription
  const updatePrescriptionData = async (values) => {
    const medicationData = {
      ...values,
      durtion: values?.durtion + values?.durationMeasurement,
      dosage: values?.dosage + values?.dosageMeasurement,
      frequency: values?.frequency + values?.frequencyMeasurement,
    };

    const pharmacyData = {
      pharmacy_name: values?.pharmacy_name,
      pharmacy_contact: values?.pharmacy_contact,
      w_id,
    };

    await Promise.all([
      updateMedication(id, medicationData),
      updatePharmacy(values?.pharmacy_id, pharmacyData),
    ]);

    return { success: true };
  };

  const getPetNames = async () => {
    try {
      const res = await getPetNameByWorkspaceIdPres(w_id);
      const filteredData = res?.data?.data?.map((item) => {
        return {
          value: item?.id,
          label: item?.name,
        };
      });
      setNameOptions(filteredData);
    } catch (error) {
      console.error("Error fetching pet data: ", error);
    }
  };

  const getAllVetNames = async () => {
    try {
      const res = await getAllVetnamePres(w_id);
      const uniqueValues = res?.data?.data?.filter(
        (obj, index, self) => index === self.findIndex((el) => el.id === obj.id)
      );
      let vetNames = [];
      vetNames = uniqueValues?.map((item) => {
        return {
          value: item?.id,
          label: item?.vet_full_name,
        };
      });
      
      // Only add "Add a new contact" option in add mode
      if (!isEditMode) {
        vetNames?.push({ value: "vet", label: "Add a new contact" });
      }
      
      setVetNameOptions(vetNames);
    } catch (error) {
      console.error("Error fetching vet data: ", error);
    }
  };

  const getAllPharmacyNames = async () => {
    try {
      const res = await getPharmacyByWorkspaceId(w_id);
      let PharmacyNames = [];
      PharmacyNames = res?.data?.data?.map((item) => {
        return {
          value: item?.id,
          label: item?.pharmacy_name,
        };
      });
      
      // Only add "Add a new Pharmacy" option in add mode
      if (!isEditMode) {
        PharmacyNames?.push({
          value: "Add a new Pharmacy",
          label: "Add a new Pharmacy",
        });
      }
      
      setPharmacyOptions(PharmacyNames);
    } catch (error) {
      console.error("Error fetching pharmacy data: ", error);
    }
  };

  useEffect(() => {
    getPetNames();
    getAllVetNames();
    getAllPharmacyNames();
    if (isEditMode) {
      loadPrescriptionDetails();
    }
  }, [w_id, isEditMode]);

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      if (isEditMode) {
        await updatePrescriptionData(values);
        showSnackbar({
          type: "success",
          message: "Prescription updated successfully.",
        });
      } else {
        if (values?.pharmacy_id === "") {
          let pharmacyId;
          if (values?.pharmacy_name !== "") {
            pharmacyId = await sendPharmacyData(values, setSubmitting);
          }
          await sendMedicationData(values, pharmacyId);
        } else {
          // User selected an existing pharmacy from the dropdown
          await sendMedicationData(values, values?.pharmacy_id);
        }
      }
    } catch (error) {
      showSnackbar({
        type: "error",
        message: isEditMode ? "Prescription update failed." : "Prescription add failed.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Handle print functionality
  const handlePrint = useReactToPrint({
    content: () => componentPDF.current,
    documentTitle: "Prescription Information",
    pageStyle: `
      @page { size: A3; margin: 200px; }
      body { margin: 100px; }
      .print-text-field { font-size: 14px; }
    `,
  });

  return (
    <FormLayout title={isEditMode ? "Edit Prescription and Medication Information" : "Prescription and Medication Information"}>
      <Formik
        initialValues={initialValue}
        onSubmit={handleSubmit}
        validationSchema={isEditMode ? EditPrescriptionFormValidation : PrescriptionFormValidation}
        enableReinitialize
        innerRef={formRef}
      >
        {({ getFieldProps, values, isSubmitting, setFieldValue, errors }) => (
          <Form>
            <Box sx={FormHeaderBox(theme.palette.primary.main)}>
              <Box sx={{ borderBottom: `1px solid ${theme.palette.primary.main}`, width: "100%" }}>
                <Typography variant="h6" sx={FormHeaderTitle}>
                  Prescription and Medication Details
                </Typography>
              </Box>
              <div className="scroll2" ref={componentPDF} style={{ width: "100%"}}>
                <Body style={{ gap: "10px 20px" }}>
                  <Column>
                    <FormGroup name="pet_id" label={"Pet Name"} requiredField>
                      <AutoComplete
                        {...getFieldProps("pet_id")}
                        options={nameOptions}
                        placeholder="Pet Name"
                        value={isEditMode ? nameOptions.find(option => option.value === values.pet_id) : undefined}
                      />
                    </FormGroup>
                  </Column>
                  <Column>
                    <FormGroup
                      name="type"
                      label={"Medication Type"}
                      requiredField
                    >
                      <InputField {...getFieldProps("type")} />
                    </FormGroup>
                  </Column>
                  <Column>
                    <FormGroup
                      name="medication_name"
                      label={"Medication Name"}
                      requiredField
                    >
                      <InputField {...getFieldProps("medication_name")} />
                    </FormGroup>
                  </Column>
                  <Column>
                    <FormGroup
                      name="vet_id"
                      label={"Prescribing Vet"}
                      requiredField
                    >
                      <AutoComplete
                        placeholder="Select Vet"
                        options={vetNameOptions}
                        {...getFieldProps("vet_id")}
                        onChange={!isEditMode ? handleSelectChange : handleVetSelection}
                        isOnChange={true}
                        value={isEditMode ? vetNameOptions.find(option => option.value === values.vet_id) : undefined}
                      />
                    </FormGroup>
                  </Column>
                  <Column>
                    <Box
                      sx={{ ...newSmallFieldBoxStyle, paddingBottom: "0px" }}
                    >
                      <Box style={{ width: "50%" }}>
                        <FormGroup
                          name="frequency"
                          label={"Frequency"}
                          requiredField
                        >
                          <InputField {...getFieldProps("frequency")} />
                        </FormGroup>
                      </Box>
                      <Box style={{ paddingLeft: "5px", width: "50%" }}>
                        <FormGroup name="frequencyMeasurement">
                          <Box sx={{ marginTop: "21px" }}></Box>
                          <AutoComplete
                            {...getFieldProps("frequencyMeasurement")}
                            options={frequencyOptions}
                            otherType="Other"
                            placeholder="Select Frequency"
                          />
                        </FormGroup>
                      </Box>
                    </Box>
                  </Column>

                  <Column>
                    <Box
                      sx={{ ...newSmallFieldBoxStyle, paddingBottom: "0px" }}
                    >
                      <Box style={{ width: "50%" }}>
                        <FormGroup name="dosage" label={"Dosage"} requiredField>
                          <InputField {...getFieldProps("dosage")} />
                        </FormGroup>
                      </Box>
                      <Box style={{ paddingLeft: "5px", width: "50%" }}>
                        <FormGroup name="dosageMeasurement">
                          <Box sx={{ marginTop: "21px" }}></Box>
                          <AutoComplete
                            {...getFieldProps("dosageMeasurement")}
                            options={dosageOptions}
                            otherType="Other"
                            placeholder="Select Dosage"
                          />
                        </FormGroup>
                      </Box>
                    </Box>
                  </Column>
                  <Column>
                    <FormGroup
                      name="start_date"
                      label={"Start Date"}
                      requiredField
                    >
                      <InputField
                        type="date"
                        {...getFieldProps("start_date")}
                        inputProps={{ max: maxDate }}
                      />
                    </FormGroup>
                  </Column>
                  <Column>
                    <Box
                      sx={{ ...newSmallFieldBoxStyle, paddingBottom: "0px" }}
                    >
                      <Box style={{ width: "50%" }}>
                        <FormGroup
                          name="durtion"
                          label={"Duration"}
                        >
                          <InputField {...getFieldProps("durtion")} />
                        </FormGroup>
                      </Box>
                      <Box style={{ paddingLeft: "5px", width: "50%" }}>
                        <FormGroup name="durationMeasurement">
                          <Box sx={{ marginTop: "21px" }}></Box>
                          <AutoComplete
                            {...getFieldProps("durationMeasurement")}
                            options={durationOptions}
                            otherType="Other"
                            placeholder="Select Duration"
                          />
                        </FormGroup>
                      </Box>
                    </Box>
                  </Column>
                  <Column>
                    {!isEditMode && pharmacyOpen ? (
                      <FormGroup name="pharmacy_name" label={"Pharmacy Name"}>
                        <InputField
                          {...getFieldProps("pharmacy_name")}
                          onChange={(e) => {
                            setFieldValue("pharmacy_name", e.target.value);
                          }}
                          InputProps={{
                            endAdornment: (
                              <IconButton
                                position="end"
                                onClick={() => {
                                  setPharmacyOpen(false);
                                  setFieldValue("pharmacy_name", "");
                                  setFieldValue("pharmacy_id", "");
                                }}
                                sx={{
                                  color: "#fff",
                                  backgroundColor: "transparent",
                                  zIndex: 1,
                                  padding: 0,
                                  cursor: "pointer",
                                  width: "auto",
                                }}
                              >
                                <ClearIcon
                                  sx={{ backgroundColor: `${theme.palette.primary.main}` }}
                                />
                              </IconButton>
                            ),
                          }}
                        />
                      </FormGroup>
                    ) : (
                      <FormGroup name="pharmacy_id" label={"Pharmacy Name"}>
                        <AutoComplete
                          {...getFieldProps("pharmacy_id")}
                          options={pharmacyOptions}
                          onChange={!isEditMode ? handlePharmacyName : handlePharmacySelection}
                          isOnChange={true}
                          value={isEditMode ? pharmacyOptions.find(option => option.value === values.pharmacy_id) : undefined}
                        />
                      </FormGroup>
                    )}
                  </Column>
                  <Column>
                    <FormGroup
                      name="pharmacy_contact"
                      label={"Pharmacy Phone Number"}
                    >
                      <CustomPhoneInput
                        {...getFieldProps("pharmacy_contact")}
                      />
                    </FormGroup>
                  </Column>
                  <Column>
                    <FormGroup
                      name="prescripton_number"
                      label={"Prescription Number"}
                    >
                      <InputField {...getFieldProps("prescripton_number")} />
                    </FormGroup>
                  </Column>
                </Body>
                <Body style={{ gap: "10px 20px", marginTop: "10px" }}>
                  <Column>
                    <FormGroup
                      name="reason"
                      label={"Reason for Use"}
                      requiredField
                    >
                      <TextArea
                        rows={10}
                        placeholder="Enter the reason"
                        inputProps={{ maxLength: 300 }}
                        {...getFieldProps("reason")}
                      />
                    </FormGroup>
                  </Column>
                </Body>
              </div>
              <Box sx={ButtonBoxStyle(`${theme.palette.primary.main}`)}>
                <Box display="flex">
                  <CustomButton
                    label="Cancel"
                    type="button"
                    style={CancelButton(`${theme.palette.primary.main}`)}
                    onClick={() => navigate(-1)}
                  />
                  <CustomButton
                    label={isSubmitting ? (isEditMode ? "Saving..." : "Submitting...") : (isEditMode ? "Save" : "Submit")}
                    disabled={isSubmitting}
                    type="submit"
                  />
                  {isEditMode && (
                    <CustomButton
                      startIcon={<LocalPrintshopOutlinedIcon />}
                      onClick={handlePrint}
                    />
                  )}
                </Box>
              </Box>
            </Box>
          </Form>
        )}
      </Formik>
    </FormLayout>
  );
}





